'use client';

import classNames from 'classnames/dedupe';
import Link from 'next/link';
import {
  type ComponentProps,
  type ElementType,
  type MouseEventHandler,
  type ReactElement,
  useMemo,
} from 'react';

type CustomAttributes = {
  'data-product-handle'?: string;
  'data-universal-retailer-id'?: string;
};

type Props<
  HrefType extends string | undefined = string,
  T = (HrefType extends string
    ? ComponentProps<'button'>
    : ComponentProps<'a'>) &
    CustomAttributes,
> = {
  label?: React.ReactNode;
  btnStyle?: 'primary' | 'secondary' | 'tertiary' | 'neutral' | 'simple';
  icon?: string | ReactElement;
  href?: HrefType;
  attributes?: T;
  className?: classNames.Argument | classNames.ArgumentArray;
  iconSize?: string;
  replaceClass?: object;
  iconClassName?: string;
  labelClassName?: string;
  isLoading?: boolean;
};

export const BetterButton = (props: Props) => {
  const Tag = (
    props?.href ? (props.href.startsWith('/') ? Link : 'a') : 'button'
  ) as ElementType;
  const buttonClasses = (() => {
    let className =
      'ui-relative ui-h-12 ui-max-w-full !ui-w-max ui-px-6 !ui-flex ui-items-center ui-justify-center ui-gap-2 ui-border-solid ui-border ui-border-transparent !ui-text-sm !ui-font-semibold ui-font-[inherit] !ui-leading-none ui-tracking-wide ui-no-underline ui-select-none ui-whitespace-nowrap ui-normal-case ui-transition-colors focus:!ui-border-solid focus:!ui-border focus:!ui-outline-0 focus-visible:!ui-outline-0 ui-cursor-pointer disabled:!ui-opacity-50 disabled:ui-cursor-not-allowed active:ui-scale-95 ';

    switch (props.btnStyle) {
      case 'primary':
        className +=
          '!ui-bg-[var(--cta-color,#030712)] hover:!ui-bg-[var(--hover-color,#111827)] !ui-text-white !ui-border-[var(--cta-color,#030712)] hover:!ui-border-[var(--hover-color,#111827)] ui-rounded-md ';
        break;

      case 'secondary':
        className += 'ui-button-secondary';
        break;

      case 'tertiary':
        className += 'ui-button-tertiary';
        break;

      case 'neutral':
        className +=
          '!ui-bg-white hover:!ui-bg-gray-100 !ui-text-gray-950 !ui-border-gray-700 ui-rounded-md';
        break;

      case 'simple':
        className =
          'ui-flex ui-items-center ui-gap-2 !ui-text-sm !ui-bg-transparent !ui-border-0 !ui-text-gray-950 focus:!ui-outline-0 focus-visible:!ui-outline-0';
        break;

      default:
        className +=
          '!ui-bg-gray-950 hover:!ui-bg-gray-900 !ui-text-white !ui-border-gray-950 hover:!ui-border-gray-900 ui-rounded-md';
        break;
    }

    Object.entries(props.replaceClass || {}).forEach(([key, value]) => {
      className = className.replaceAll(key, value);
    });

    return className;
  })();

  const handleClick: MouseEventHandler<
    HTMLAnchorElement | HTMLButtonElement
  > = (event) => {
    if (props?.attributes?.['aria-disabled']) {
      event.preventDefault();
      event.stopPropagation();
    } else if (props?.attributes?.onClick) {
      if (event.target instanceof HTMLButtonElement) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        props?.attributes?.onClick(event as any);
      } else if (event instanceof HTMLAnchorElement) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        props?.attributes?.onClick(event as any);
      }
    }
  };

  const Label = useMemo(() => {
    if (!props?.label) {
      return null;
    }

    if (typeof props.label === 'string') {
      return (
        <span
          id={`${props?.attributes?.id ?? ''}label_span`}
          className={`${props.labelClassName || ''}`}
          dangerouslySetInnerHTML={{
            __html: props.label,
          }}
        />
      );
    }
    if (typeof props?.label === 'object') {
      return props.label;
    }

    return null;
  }, [props?.label, props?.labelClassName, props?.attributes?.id]);

  return (
    <Tag
      className={classNames(buttonClasses, props.className)}
      href={props?.href}
      onClick={handleClick}
      {...props.attributes}
    >
      {props.icon &&
        (typeof props.icon === 'string' ? (
          <span
            className={classNames(
              {
                [props.iconClassName ?? '']: props.iconClassName,
                'ui-invisible': props.isLoading,
              },
              `ui-w-${props.iconSize || '3'} ui-flex ui-items-center ui-justify-center [&_svg]:ui-w-full`
            )}
            dangerouslySetInnerHTML={{
              __html: props.icon,
            }}
            id={`${props?.attributes?.id ?? ''} icon_span`}
          />
        ) : (
          props.icon
        ))}

      <span
        className={classNames(
          {
            'ui-invisible': props.isLoading,
            'ui-hidden': !Label,
          },
          'ui-flex ui-items-center'
        )}
      >
        {Label}
      </span>

      <span
        className={classNames(
          {
            'ui-hidden': !props.isLoading,
          },
          'ui-absolute ui-top-1/2 ui-left-1/2 -ui-translate-x-1/2 -ui-translate-y-1/2'
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          className="ui-w-4 ui-animate-spin"
        >
          <path
            className="fa-secondary"
            opacity=".4"
            d="M0 256C0 397.4 114.6 512 256 512c94.7 0 177.5-51.5 221.7-128l-55.4-32c-33.2 57.4-95.2 96-166.3 96C150 448 64 362 64 256S150 64 256 64l0-64C114.6 0 0 114.6 0 256z"
          />
          <path
            className="fa-primary"
            d="M477.7 384c21.8-37.7 34.3-81.4 34.3-128C512 114.6 397.4 0 256 0l0 64c106 0 192 86 192 192c0 35-9.4 67.8-25.7 96l55.4 32z"
          />
        </svg>
      </span>
    </Tag>
  );
};
